import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {Controller, useForm} from 'react-hook-form';
import {Container, Header, Form, Popup, Label, Button, Grid} from 'semantic-ui-react';
import * as yup from 'yup';

import {useReservedHostnames, useSupportedDrupalVersions} from 'hooks';

import {
  validationSchemas,
  drupalSubdomain,
  formatDrupalVersionsOptions,
} from '../../Common/sharedElements';

import styles from './EnvironmentCreation.module.scss';

const formSchema = yup.object().shape({
  name: validationSchemas.drupalNameValidation.test(
    'domainIsUnique',
    'Such domain already exists',
    (value, ctx) => {
      const siteDomain = value + drupalSubdomain;

      if (ctx.options.context.reservedHostnames.includes(value)) {
        return ctx.createError({message: 'Provided site name is a reserved name'});
      }

      return !ctx.options.context.reservedFullyQualifiedDomainNames.includes(siteDomain);
    }
  ),
  cloneFrom: yup.string().required('Site name to clone from is required'),
});

const defaultFormValues = {
  cloneFrom: '',
  name: '',
  version: '',
};

const EnvironmentCreation = ({create, cloneOptions}) => {
  const {
    reservedHostnames,
    reservedFullyQualifiedDomainNames,
    isLoading: isLoadingReservedHostnames,
  } = useReservedHostnames('drupal');
  const {
    supportedDrupalVersions,
    isLoading: isLoadingSupportedVersions,
    error: supportedDrupalVersionError,
  } = useSupportedDrupalVersions();

  const drupalVersionsOptions = formatDrupalVersionsOptions(supportedDrupalVersions);

  const versionSchema = yup
    .string()
    .required('Please choose a CERN Drupal version for the new environment')
    .oneOf(supportedDrupalVersions, 'Version must be one of supported CERN Drupal versions')
    .test(
      'cloneSourceVersionIncompatibility',
      'Version of the clone must be higher than the version of the source site',
      (value, ctx) => {
        const originalSiteVersion = cloneOptions.find(
          obj => obj.value === ctx.from[0].value.cloneFrom
        )?.version;

        return (
          value.localeCompare(originalSiteVersion, undefined, {
            numeric: true,
            sensitivity: 'base',
          }) >= 0
        );
      }
    );

  const {
    control,
    handleSubmit,
    formState: {isValid, isSubmitting},
  } = useForm({
    context: {reservedHostnames, reservedFullyQualifiedDomainNames},
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formSchema.concat(yup.object().shape({version: versionSchema}))),
  });

  // Form submission handler, sends a POST request via a function passed from parent component in props.create
  const submitHandler = async data => {
    await create({
      name: data.name,
      cloneFrom: data.cloneFrom,
      version: data.version,
    });
  };

  return (
    <Container className={styles.EnvironmentCreation}>
      <Header as="h5" dividing>
        New Environment
      </Header>

      <Form onSubmit={handleSubmit(submitHandler)} loading={isSubmitting}>
        <Grid centered stackable>
          <Grid.Row columns={2}>
            <Grid.Column textAlign="right" width={5}>
              <div className={styles.FixedLabel}>
                <Popup
                  trigger={<label htmlFor="cloneFrom">Clone from*</label>}
                  position="left center"
                >
                  <Popup.Content>Choose the environment to clone from.</Popup.Content>
                </Popup>
              </div>
            </Grid.Column>
            <Grid.Column textAlign="left" width={11}>
              <Controller
                control={control}
                name="cloneFrom"
                render={({field: {onChange, value, name}, fieldState: {error}}) => (
                  <Form.Dropdown
                    name={name}
                    value={value}
                    search
                    selection
                    fluid
                    required
                    noResultsMessage="No sites available"
                    placeholder="Site name to clone from"
                    error={error?.message}
                    options={cloneOptions}
                    onChange={(_evt, data) => onChange(data.value)}
                  />
                )}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={2}>
            <Grid.Column textAlign="right" width={5}>
              <div className={styles.FixedLabel}>
                <Popup trigger={<label htmlFor="name">URL*</label>} position="left center">
                  <Popup.Content>
                    Choose descriptive and short name, following naming conventions.
                  </Popup.Content>
                </Popup>
              </div>
            </Grid.Column>
            <Grid.Column textAlign="left" width={11}>
              <Controller
                control={control}
                name="name"
                render={({field: {onChange, value, name}, fieldState: {error}}) => (
                  <Form.Input
                    id="siteName"
                    error={error?.message}
                    required
                    onChange={(_evt, data) => onChange(data.value)}
                    name={name}
                    value={value}
                    labelPosition="right"
                  >
                    <input />
                    <Label>{drupalSubdomain}</Label>
                  </Form.Input>
                )}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={3}>
            <Grid.Column textAlign="right" width={5}>
              <div className={styles.FixedLabel}>
                <Popup
                  trigger={<label htmlFor="version">CERN Drupal version*</label>}
                  position="left center"
                >
                  <Popup.Content>
                    Choose the CERN Drupal version for the new environment. The vX.Y matches the
                    Drupal Major.Minor release version.
                  </Popup.Content>
                </Popup>
              </div>
            </Grid.Column>
            <Grid.Column textAlign="left" width={5} className={styles.SupportedDrupalVersions}>
              <Controller
                control={control}
                name="version"
                render={({field: {onChange, value, name}, fieldState: {error}}) => (
                  <Form.Select
                    id="version"
                    name={name}
                    value={value}
                    error={
                      error?.message ||
                      (supportedDrupalVersionError
                        ? 'There was a problem while fetching supported drupal versions. Please wait a couple of minutes and refresh the page.'
                        : undefined)
                    }
                    fluid
                    options={drupalVersionsOptions}
                    onChange={(_evt, data) => onChange(data.value)}
                    loading={isLoadingSupportedVersions}
                  />
                )}
              />
            </Grid.Column>
            <Grid.Column width={6}></Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column textAlign="right">
              <Button
                type="submit"
                className={styles.SaveButton}
                disabled={
                  !isValid ||
                  isLoadingReservedHostnames ||
                  isLoadingSupportedVersions ||
                  isSubmitting
                }
              >
                Create
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </Container>
  );
};

EnvironmentCreation.propTypes = {
  create: PropTypes.func.isRequired,
  cloneOptions: PropTypes.array.isRequired,
};

export default EnvironmentCreation;
